/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import classnames from 'classnames';
import { isFunction } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { getElementDirection, getElementInfo, getElementPosition } from 'utils/element';
import { expandDataProps } from 'utils/helper/common';
import { styleOnlySanitizeGenericHtml } from 'utils/sanitize';

import type {
  Data,
  ElementDirection,
  TooltipDisplay,
  TooltipTextAlign,
  TooltipTrigger,
  TooltipWidth
} from 'types';

type Props = {
  component?: React.ReactNode,
  message?: string,
  trigger: TooltipTrigger,
  width: TooltipWidth,
  display: TooltipDisplay,
  textAlign: TooltipTextAlign,
  classNameWrapper?: string,
  className?: string,
  data?: Data,
  children?: React.ReactNode,
  showQuestionMark: boolean,
  tooltipDirection?: ElementDirection,
  forceHide?: boolean,
  questionMarkCustomSize: {
    width: number,
    height: number
  },
  overflowWrap?: boolean,
  onClick?: () => void
};

type State = {
  x: number,
  y: number,
  message?: string,
  direction: ElementDirection,
  width: string,
  textAlign: TooltipTextAlign,
  show: boolean
};

class Tooltip extends React.Component<Props, State> {
  static defaultProps = {
    width: 'normal',
    message: '',
    display: 'normal',
    textAlign: 'center',
    trigger: 'hover',
    showQuestionMark: false,
    questionMarkCustomSize: {
      width: 12,
      height: 12
    }
  };

  state: State = {
    x: 0,
    y: 0,
    direction: {
      vertical: 'bottom',
      horizontal: 'right'
    },
    width: this.props.width,
    textAlign: this.props.textAlign,
    show: false
  };

  tooltipContainer = document.createElement('div');
  tooltipTargetElement: HTMLElement | null = null;
  tooltipElement: HTMLElement | null = null;

  componentDidUpdate(prevProps: Props) {
    const { forceHide } = this.props;

    if (forceHide !== prevProps.forceHide) {
      this.setState({
        show: false
      });
    }
  }

  componentWillUnmount() {
    if (document.body && document.body.contains(this.tooltipContainer)) {
      document.body.removeChild(this.tooltipContainer);
    }
  }

  handleEnter = (): void => {
    const { tooltipDirection, onClick, trigger } = this.props;

    if (document.body && !document.body.contains(this.tooltipContainer)) {
      document.body.appendChild(this.tooltipContainer);
    }

    const position = getElementPosition(this.tooltipTargetElement, this.tooltipElement);
    const direction = tooltipDirection || getElementDirection(this.tooltipTargetElement, this.tooltipElement);

    if (this.tooltipTargetElement && position && direction) {
      const targetElementInfo = getElementInfo(this.tooltipTargetElement);
      const elementInfo = getElementInfo(this.tooltipElement);

      if (targetElementInfo && elementInfo) {
        const elementHalfWidth = elementInfo.width / 2;
        const topOffset = direction.vertical === 'top' ? -position : position;
        const middleOffset = direction.horizontal === 'center' ? targetElementInfo.width / 2 : 0;
        const leftOffset = direction.horizontal === 'left' ? -(elementInfo.width - elementHalfWidth / 2) : 0;
        const rightOffset = direction.horizontal === 'right' ? elementHalfWidth : 0;

        this.setState({
          x: (targetElementInfo.left + window.pageXOffset) + leftOffset + middleOffset + rightOffset,
          y: (targetElementInfo.top + window.pageYOffset) + topOffset,
          direction,
          show: true
        });

        if (trigger === 'click' && isFunction(onClick)) {
          onClick();
        }
      }
    }
  };

  handleLeave = (): void => {
    this.setState({
      show: false
    });
  };

  render(): React.ReactNode {
    const {
      component,
      display,
      trigger,
      showQuestionMark,
      questionMarkCustomSize,
      classNameWrapper,
      className,
      data,
      children,
      onClick,
      overflowWrap
    } = this.props;

    const { x, y, direction, width, textAlign, show } = this.state;
    const { message } = this.props;

    if (!message && !component) {
      return children || null;
    }

    const triggerProps: {
      onMouseEnter?: () => void,
      onClick?: () => void
    } = {};

    if (trigger === 'hover') {
      triggerProps.onMouseEnter = this.handleEnter;
      triggerProps.onClick = onClick;
    } else {
      triggerProps.onClick = this.handleEnter;
    }

    const tooltipWrapperClasses = classnames({
      'tooltip-wrapper': true,
      [`tooltip-wrapper--display-${display}`]: display,
      [`${String(classNameWrapper)}`]: classNameWrapper
    });

    const tooltipClasses = classnames({
      'tooltip': true,
      'tooltip--visible': show,
      [`tooltip--visible-${direction.vertical}`]: show && direction.vertical,
      [`tooltip--visible-${direction.horizontal}`]: show && direction.horizontal,
      [`tooltip--visible-${direction.horizontal}-${width}`]: width,
      [`tooltip--text-align-${textAlign}`]: textAlign,
      [`tooltip--width-${width}`]: width,
      [`${String(className)}`]: className,
      'tooltip--break-word': overflowWrap
    });

    return (
      <section
        className={tooltipWrapperClasses}
        {...triggerProps}
        onMouseLeave={this.handleLeave}
        ref={ref => { this.tooltipTargetElement = ref; }}
        {...expandDataProps(data)}
      >
        {
          // $FlowIgnore
          ReactDOM.createPortal(
            <div
              className={tooltipClasses}
              style={{ top: y, left: x }}
              ref={ref => { this.tooltipElement = ref; }}
              data-qa="tooltip"
            >
              {
                message ?
                  <div dangerouslySetInnerHTML={{ __html: styleOnlySanitizeGenericHtml(message) }} /> :
                  component
              }
            </div>,
            this.tooltipContainer
          )
        }
        {children}
        {
          showQuestionMark && (
            <SVGDynamicIcon
              name="question-mark-inside-circle-custom-bg-white-fg"
              width={questionMarkCustomSize.width}
              height={questionMarkCustomSize.height}
              customColor={show ? 'black' : 'grey-light'}
            />
          )
        }
      </section>
    );
  }
}

export default Tooltip;
